import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { isEqual } from 'lodash';
import { forwardRef, Fragment, memo, useCallback, useMemo } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary } from 'shell/components/Accordion';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Divider from 'shell/components/Divider';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import MenuItem from 'shell/components/MenuItem';
import Select from 'shell/components/Select';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import TextArea from 'shell/components/TextArea';
import TextInput from 'shell/components/TextInput';
import Tooltip from 'shell/components/Tooltip';
import { RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getWordCount, roundTo } from 'utils/helper';
import { getRubricOptionLevel } from 'utils/rubricScoring';

const MemoText = memo(({ text, variant = 'p2' }) => {
    return <Text variant={variant}>{text}</Text>;
});

const MemoOption = memo(
    ({ index, disabled, criterion, option, errors, onChange, onClickAddOption, onClickDeleteOption }) => {
        const { device } = useViewport();
        const { t } = useTranslation('assignment');

        const handleChange = useCallback(
            (e) => onChange(criterion.id, option.id, e.target.name, e.target.value),
            [criterion.id, onChange, option.id]
        );

        const handleClickDeleteOption = useCallback(
            () => onClickDeleteOption(criterion.id, option.id),
            [criterion.id, onClickDeleteOption, option.id]
        );

        const handleClickAddOption = useCallback(
            () => onClickAddOption(criterion.id, index + 1),
            [criterion.id, index, onClickAddOption]
        );

        return (
            <Fragment key={option.id}>
                <OptionStack id={option.id} alignItems={'flex-end'} device={device} spacing={2}>
                    <TextInput
                        aria-label={`points for criterion ${criterion.index + 1} option ${index + 1}`}
                        disabled={disabled}
                        error={Boolean(errors?.[criterion.id]?.[option.id]?.rate)}
                        helperText={errors?.[criterion.id]?.[option.id]?.rate}
                        label={t('rubric.points-label', 'Points')}
                        name={'rate'}
                        placeholder={t('rubric.table.points-placeholder', 'ex. 10')}
                        required
                        size={'small'}
                        value={option.rate}
                        onChange={handleChange}
                    />
                    <TextInput
                        aria-label={`title for criterion ${criterion.index + 1} option ${index + 1}`}
                        disabled={disabled}
                        label={t('rubric.table.title-label', 'Title')}
                        name={'title'}
                        placeholder={t('rubric.table.title-placeholder', 'Enter title')}
                        size={'small'}
                        value={option.title}
                        onChange={handleChange}
                    />
                    <CustomTextArea
                        aria-label={`description for criterion ${criterion.index + 1} option ${index + 1}`}
                        disabled={disabled}
                        error={Boolean(errors?.[criterion.id]?.[option.id]?.description)}
                        helperText={t('rubric.table.description-helper-text', '{{wordCountStatus}} words', {
                            wordCountStatus: `${getWordCount(option.description)}/${RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT}`,
                        })}
                        label={t('rubric.table.description-label', 'Description')}
                        name={'description'}
                        placeholder={t('rubric.table.description-placeholder', 'Enter description')}
                        rows={6}
                        size={'small'}
                        value={option.description}
                        onChange={handleChange}
                    />
                    <IconButton
                        aria-label={'delete option'}
                        disabled={disabled || criterion.options.length <= 2}
                        kind={'delete'}
                        size={'small'}
                        variant={'square'}
                        onClick={handleClickDeleteOption}
                    />
                </OptionStack>
                <CustomDivider
                    device={device}
                    flexItem
                    isLast={index === criterion.options.length - 1}
                    orientation={device === 'mobile' ? 'horizontal' : 'vertical'}
                    role={'presentation'}
                >
                    <IconButton
                        aria-label={'add a new option'}
                        disabled={disabled}
                        kind={'add'}
                        size={'small'}
                        variant={'circle'}
                        onClick={handleClickAddOption}
                    />
                </CustomDivider>
            </Fragment>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.index === nextProps.index &&
            prevProps.disabled === nextProps.disabled &&
            prevProps.criterion.id === nextProps.criterion.id &&
            prevProps.criterion.index === nextProps.criterion.index &&
            prevProps.criterion.options.length === nextProps.criterion.options.length &&
            isEqual(prevProps.option, nextProps.option) &&
            isEqual(prevProps.errors, nextProps.errors) &&
            prevProps.sort === nextProps.sort
        );
    }
);

const MemoUnscoredOptions = memo(
    ({ disabled, criterion, errors, onChange, onClickAddOption, onClickDeleteOption }) => {
        const { device } = useViewport();
        const { t } = useTranslation('assignment');
        const isMobile = device === 'mobile';

        return (
            <UnscoredOptionsStack id={`${criterion.id}-stack`}>
                {criterion.options.map((option, index) => {
                    const handleChange = (e) => onChange(criterion.id, option.id, e.target.name, e.target.value);
                    const handleClickDeleteOption = () => onClickDeleteOption(criterion.id, option.id);
                    const handleClickAddOption = () => onClickAddOption(criterion.id, index + 1);

                    return (
                        <Fragment key={option.id}>
                            <UnscoredOptionStack
                                id={option.id}
                                alignItems={isMobile ? 'stretch' : 'flex-start'}
                                direction={isMobile ? 'column' : 'row'}
                                spacing={3}
                            >
                                <LevelStack spacing={1}>
                                    <Text variant={'p3'} weight={'medium'}>
                                        {t('rubric.table.level-label', 'Level')}
                                    </Text>
                                    <Text variant={'p2'}>{getRubricOptionLevel(criterion, option)}</Text>
                                </LevelStack>
                                <UnscoredFieldsStack
                                    direction={isMobile ? 'column' : 'row'}
                                    spacing={3}
                                    $isMobile={isMobile}
                                >
                                    <UnscoredTitleInput
                                        aria-label={`title for criterion ${criterion.index + 1} option ${index + 1}`}
                                        disabled={disabled}
                                        label={t('rubric.table.title-label', 'Title')}
                                        name={'title'}
                                        placeholder={t('rubric.table.title-placeholder', 'Enter title')}
                                        size={'small'}
                                        value={option.title}
                                        onChange={handleChange}
                                    />
                                    <UnscoredDescriptionArea
                                        aria-label={`description for criterion ${criterion.index + 1} option ${index + 1}`}
                                        disabled={disabled}
                                        error={Boolean(errors?.[criterion.id]?.[option.id]?.description)}
                                        helperText={t(
                                            'rubric.table.description-helper-text',
                                            '{{wordCountStatus}} words',
                                            {
                                                wordCountStatus: `${getWordCount(
                                                    option.description
                                                )}/${RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT}`,
                                            }
                                        )}
                                        helperTextPosition={'end'}
                                        label={t('rubric.table.description-label', 'Description')}
                                        name={'description'}
                                        placeholder={t('rubric.table.description-placeholder', 'Enter description')}
                                        rows={4}
                                        size={'small'}
                                        value={option.description}
                                        onChange={handleChange}
                                    />
                                </UnscoredFieldsStack>
                                <DeleteOptionButton
                                    aria-label={'delete option'}
                                    disabled={disabled || criterion.options.length <= 2}
                                    kind={'delete'}
                                    size={'small'}
                                    variant={'square'}
                                    onClick={handleClickDeleteOption}
                                />
                            </UnscoredOptionStack>
                            {index < criterion.options.length - 1 && (
                                <UnscoredDivider flexItem role={'presentation'}>
                                    <IconButton
                                        aria-label={'add a new option'}
                                        disabled={disabled}
                                        kind={'add'}
                                        size={'small'}
                                        variant={'circle'}
                                        onClick={handleClickAddOption}
                                    />
                                </UnscoredDivider>
                            )}
                        </Fragment>
                    );
                })}
            </UnscoredOptionsStack>
        );
    },
    (prevProps, nextProps) =>
        prevProps.disabled === nextProps.disabled &&
        isEqual(prevProps.criterion, nextProps.criterion) &&
        isEqual(prevProps.errors, nextProps.errors)
);

const MemoDraggable = memo(
    ({
        defaultExpanded,
        disabled,
        criterion,
        criterionIndex,
        errors,
        isUnscored,
        sort,
        setSort,
        onClickEditCriterion,
        onClickDuplicateCriterion,
        onChange,
        onClickAddOption,
        onClickDeleteOption,
    }) => {
        const { device } = useViewport();
        const { t } = useTranslation('assignment');

        const criterionMaxPoints = useMemo(
            () => Math.max(...criterion.options.map((option) => Number(option.rate))),
            [criterion.options]
        );

        const memoCriterion = useMemo(
            () => ({
                ...criterion,
                index: criterionIndex,
            }),
            [criterion, criterionIndex]
        );

        return (
            <Draggable key={criterion.id} draggableId={criterion.id} index={criterionIndex}>
                {(provided) => (
                    <CustomAccordion
                        ref={provided.innerRef}
                        defaultExpanded={defaultExpanded}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        aria-roledescription={undefined}
                        role={'presentation'}
                    >
                        <CustomAccordionSummary device={device} role={'region'}>
                            <Stack sx={{ width: '100%' }} alignItems={'center'} direction={'row'} spacing={2}>
                                <CustomIcon kind={'reorder'} />
                                {device === 'mobile' ? (
                                    <Stack
                                        sx={{ width: '100%', pr: 2 }}
                                        alignItems={'flex-start'}
                                        direction={'row'}
                                        justifyContent={'space-between'}
                                    >
                                        <Stack spacing={2}>
                                            <Stack
                                                alignItems={'center'}
                                                direction={'row'}
                                                justifyContent={'space-between'}
                                            >
                                                {!isUnscored && (
                                                    <Text>
                                                        {t('rubric.criterion-points', 'Pts')}:{' '}
                                                        {criterionMaxPoints ? `/${criterionMaxPoints}` : '-'}
                                                    </Text>
                                                )}
                                                <Stack sx={{ mt: -0.5 }} direction={'row'} spacing={2}>
                                                    <IconButton
                                                        aria-label={'edit criterion'}
                                                        disabled={disabled}
                                                        kind={'edit'}
                                                        size={'small'}
                                                        variant={'square'}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onClickEditCriterion(criterion.id);
                                                        }}
                                                    />
                                                    {onClickDuplicateCriterion && (
                                                        <IconButton
                                                            aria-label={'duplicate criterion'}
                                                            disabled={disabled}
                                                            kind={'copy'}
                                                            size={'small'}
                                                            variant={'square'}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onClickDuplicateCriterion(criterion.id);
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </Stack>
                                            <MemoText text={criterion.title} variant={'p1'} />
                                            {isUnscored && (
                                                <Text>
                                                    {t('rubric.rating-scale', 'Rating Scale: {{count}}', {
                                                        count: criterion.options.length,
                                                    })}
                                                </Text>
                                            )}
                                            <MemoText text={criterion.description} />
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack
                                        sx={{ width: '100%', pr: 2 }}
                                        alignItems={'center'}
                                        direction={'row'}
                                        justifyContent={'space-between'}
                                    >
                                        <Stack spacing={2}>
                                            <MemoText text={criterion.title} variant={'p1'} />
                                            {!isUnscored && (
                                                <Text>
                                                    {t('rubric.criterion-points', 'Pts')}:{' '}
                                                    {criterionMaxPoints ? `/${criterionMaxPoints}` : '-'}
                                                </Text>
                                            )}
                                            {isUnscored && (
                                                <Text>
                                                    {t('rubric.rating-scale', 'Rating Scale: {{count}}', {
                                                        count: criterion.options.length,
                                                    })}
                                                </Text>
                                            )}
                                            <MemoText text={criterion.description} />
                                        </Stack>
                                        <Stack direction={'row'} spacing={2}>
                                            <IconButton
                                                aria-label={'edit criterion'}
                                                disabled={disabled}
                                                kind={'edit'}
                                                size={'small'}
                                                variant={'square'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onClickEditCriterion(criterion.id);
                                                }}
                                            />
                                            {onClickDuplicateCriterion && (
                                                <IconButton
                                                    aria-label={'duplicate criterion'}
                                                    disabled={disabled}
                                                    kind={'copy'}
                                                    size={'small'}
                                                    variant={'square'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onClickDuplicateCriterion(criterion.id);
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                        </CustomAccordionSummary>
                        <CustomAccordionDetails>
                            {isUnscored ? (
                                <MemoUnscoredOptions
                                    disabled={disabled}
                                    criterion={memoCriterion}
                                    errors={errors}
                                    onChange={onChange}
                                    onClickAddOption={onClickAddOption}
                                    onClickDeleteOption={onClickDeleteOption}
                                />
                            ) : (
                                <OverflowStack
                                    id={`${criterion.id}-stack`}
                                    direction={device === 'mobile' ? 'column' : 'row'}
                                    tabIndex={0}
                                >
                                    {criterion.options.map((option, index) => (
                                        <MemoOption
                                            key={option.id}
                                            index={index}
                                            disabled={disabled}
                                            criterion={memoCriterion}
                                            option={option}
                                            errors={errors}
                                            onChange={onChange}
                                            onClickAddOption={onClickAddOption}
                                            onClickDeleteOption={onClickDeleteOption}
                                        />
                                    ))}
                                </OverflowStack>
                            )}
                        </CustomAccordionDetails>
                    </CustomAccordion>
                )}
            </Draggable>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.disabled === nextProps.disabled &&
            prevProps.isUnscored === nextProps.isUnscored &&
            isEqual(prevProps.criterion, nextProps.criterion) &&
            prevProps.criterionIndex === nextProps.criterionIndex &&
            isEqual(prevProps.errors, nextProps.errors) &&
            prevProps.sort === nextProps.sort
        );
    }
);

const CreateRubricTable = memo(
    forwardRef(
        (
            {
                data,
                defaultExpanded = true,
                disabled,
                errors,
                isUnscored = false,
                sort,
                setSort,
                onChange,
                onClickAddCriterion,
                onClickEditCriterion,
                onClickDuplicateCriterion,
                onClickAddOption,
                onClickDeleteOption,
                onDragEnd,
                onSortNow,
                ...rest
            },
            ref
        ) => {
            const { device } = useViewport();
            const { t } = useTranslation('assignment');

            const totalMaxPoints = useMemo(
                () =>
                    roundTo(
                        data
                            .map((criterion) => {
                                return Math.max(...criterion.options.map((option) => Number(option.rate)));
                            })
                            .reduce((acc, cur) => acc + cur, 0)
                    ),
                [data]
            );

            return (
                <RootStack ref={ref} {...rest}>
                    <PrimaryHeaderStack direction={'row'} justifyContent={'space-between'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Text variant={'p1'} weight={'semibold'}>
                                {t('rubric.criteria', 'Criteria')}
                            </Text>
                        </Stack>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            {device !== 'mobile' && (
                                <>
                                    <Text noWrap variant={'p3'}>
                                        {isUnscored
                                            ? t('rubric.table.sort', 'Sort')
                                            : t('rubric.table.sort-ratings', 'Sort Ratings')}
                                    </Text>
                                    <Tooltip
                                        content={
                                            <Text color={'inherit'} variant={'p2'}>
                                                {isUnscored
                                                    ? t(
                                                          'rubric.table.sort-alphabetical-tooltip',
                                                          'Rating levels will be sorted automatically when the rubric is saved, or when you click the “Sort Now” button. You can decide if you want ratings displayed in alphabetical order.'
                                                      )
                                                    : t(
                                                          'rubric.table.sort-tooltip',
                                                          'The ratings will automatically be sorted when the rubric is saved. You can decide if you want it displayed in ascending or descending order.'
                                                      )}
                                            </Text>
                                        }
                                        type={'info'}
                                    />
                                </>
                            )}
                            <CustomSelect
                                $compact={isUnscored}
                                disabled={disabled}
                                inputProps={{ 'aria-label': 'sort ratings' }}
                                size={isUnscored ? 'small' : undefined}
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                            >
                                {isUnscored
                                    ? [
                                          <MenuItem key={'asc'} value={'asc'}>
                                              {t('rubric.table.sort-a-z', 'A-Z')}
                                          </MenuItem>,
                                          <MenuItem key={'desc'} value={'desc'}>
                                              {t('rubric.table.sort-z-a', 'Z-A')}
                                          </MenuItem>,
                                      ]
                                    : [
                                          <MenuItem key={'desc'} value={'desc'}>
                                              {t('common:controls.descending', 'Descending')}
                                          </MenuItem>,
                                          <MenuItem key={'asc'} value={'asc'}>
                                              {t('common:controls.ascending', 'Ascending')}
                                          </MenuItem>,
                                      ]}
                            </CustomSelect>
                            {isUnscored && onSortNow && (
                                <Button
                                    disabled={disabled}
                                    size={'small'}
                                    startIcon={<Icon kind={'sort'} />}
                                    variant={'borderless'}
                                    onClick={onSortNow}
                                >
                                    {t('rubric.table.sort-now', 'Sort Now')}
                                </Button>
                            )}
                        </Stack>
                    </PrimaryHeaderStack>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId={'criteria-droppable'}>
                            {(provided) => (
                                <Stack ref={provided.innerRef} {...provided.droppableProps}>
                                    {data.map((criterion, criterionIndex) => {
                                        return (
                                            <MemoDraggable
                                                key={criterion.id}
                                                disabled={disabled}
                                                defaultExpanded={defaultExpanded}
                                                criterion={criterion}
                                                criterionIndex={criterionIndex}
                                                errors={errors}
                                                isUnscored={isUnscored}
                                                sort={sort}
                                                setSort={setSort}
                                                onChange={onChange}
                                                onClickAddCriterion={onClickAddCriterion}
                                                onClickEditCriterion={onClickEditCriterion}
                                                onClickDuplicateCriterion={onClickDuplicateCriterion}
                                                onClickAddOption={onClickAddOption}
                                                onClickDeleteOption={onClickDeleteOption}
                                            />
                                        );
                                    })}
                                    {provided.placeholder}
                                </Stack>
                            )}
                        </Droppable>
                    </DragDropContext>
                    <FooterBox>
                        {isUnscored ? (
                            <Stack sx={{ p: 4 }}>
                                <Button
                                    disabled={disabled}
                                    startIcon={<Icon kind={'add'} />}
                                    variant={'borderless'}
                                    onClick={onClickAddCriterion}
                                >
                                    {t('rubric.add-criterion', 'Add Criterion')}
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction={'row-reverse'} divider={<Divider flexItem orientation={'vertical'} />}>
                                <CustomText>{totalMaxPoints || '-'}</CustomText>
                                <Stack sx={{ flex: 1, p: 4 }} direction={'row'} justifyContent={'space-between'}>
                                    <Button
                                        disabled={disabled}
                                        startIcon={<Icon kind={'add'} />}
                                        variant={'borderless'}
                                        onClick={onClickAddCriterion}
                                    >
                                        {t('rubric.add-criterion', 'Add Criterion')}
                                    </Button>
                                    <Text sx={{ flex: 1 }} align={'right'}>
                                        {t('rubric.total-points', 'Total Points')}
                                    </Text>
                                </Stack>
                            </Stack>
                        )}
                    </FooterBox>
                </RootStack>
            );
        }
    )
);

const RootStack = styled(Stack)(({ theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '12px',
    };
});

const PrimaryHeaderStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        padding: theme.spacing(2),
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomSelect = styled(Select)(({ $compact, theme }) => {
    return {
        '&.MuiInputBase-root': {
            width: $compact ? 68 : 120,
        },
        '& > .MuiSelect-select': {
            padding: theme.spacing(1.5, 3),
        },
    };
});

const CustomAccordion = styled(Accordion)(({ theme }) => {
    return {
        '&:focus-visible': {
            position: 'relative',
            zIndex: 1,
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomAccordionSummary = styled(AccordionSummary)(({ theme, device }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        padding: theme.spacing(2),
        background: theme.palette.ensightful.neutral[100],
        '&:focus-visible': {
            background: theme.palette.ensightful.neutral[100],
        },
        '&:focus-within': {
            background: theme.palette.ensightful.neutral[100],
        },
        '&.Mui-expanded': {
            borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        },
        ...(device === 'mobile' && {
            alignItems: 'flex-start',
        }),
    };
});

const CustomIcon = styled(Icon)(() => {
    return {
        fontSize: '0.75rem',
    };
});

const CustomAccordionDetails = styled(AccordionDetails)(() => {
    return {
        margin: 0,
    };
});

const OverflowStack = styled(Stack)(({ theme }) => {
    return {
        overflowX: 'auto',
        '&:focus-visible': {
            position: 'relative',
            zIndex: 1,
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const OptionStack = styled(Stack)(({ device, theme }) => {
    return {
        padding: theme.spacing(3, 5),
        ...(device === 'mobile'
            ? {
                  width: '100%',
              }
            : {
                  minWidth: 240,
                  maxWidth: 240,
              }),
    };
});

const CustomTextArea = styled(TextArea)(() => {
    return {
        '& > p.MuiFormHelperText-root': {
            textAlign: 'right',
        },
    };
});

const UnscoredOptionsStack = styled(Stack)(() => ({
    width: '100%',
}));

const UnscoredOptionStack = styled(Stack)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(2, 2, 2, 4),
    background: theme.palette.ensightful.shades[0],
}));

const LevelStack = styled(Stack)(({ theme }) => ({
    flex: '0 0 67px',
    minWidth: 67,
    [theme.breakpoints.down('tablet')]: {
        flex: '0 0 auto',
    },
}));

const UnscoredFieldsStack = styled(Stack)(({ $isMobile }) => ({
    flex: $isMobile ? '0 0 auto' : 1,
    minWidth: 0,
    width: '100%',
    ...(!$isMobile && {
        alignItems: 'flex-start',
    }),
}));

const UnscoredTitleInput = styled(TextInput)(({ theme }) => ({
    flex: '0 0 195px',
    width: 195,
    [theme.breakpoints.down('tablet')]: {
        flex: '0 0 auto',
        width: '100%',
    },
}));

const UnscoredDescriptionArea = styled(TextArea)(() => ({
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
}));

const DeleteOptionButton = styled(IconButton)(({ theme }) => ({
    marginTop: theme.spacing(4.5),
    [theme.breakpoints.down('tablet')]: {
        alignSelf: 'flex-end',
        marginTop: 0,
    },
}));

const UnscoredDivider = styled(Divider)(({ theme }) => ({
    minHeight: 24,
    margin: theme.spacing(0, 2),
}));

const CustomDivider = styled(Divider)(({ device, isLast }) => {
    return {
        ...(device === 'mobile' &&
            isLast && {
                marginBottom: 14,
            }),
    };
});

const FooterBox = styled(Box)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '0 0 12px 12px',
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

export default CreateRubricTable;
